import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { livyConfig, livyFetch } from 'src/lib/livyTransport';
import { tokenForCompany } from 'src/lib/livyApi';
import { shouldSearch } from './commands';
import { api } from 'src/api';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';

/** A resident to run the resident support view as, from Livy's own search (the selected company only). */

/** Residents with an account only: a pin needs both ids, and the MCP service proves them before any read. */
export const residentsFrom = data => {
  var _data$results$residen, _data$results;
  return ((_data$results$residen = (_data$results = data.results) === null || _data$results === void 0 ? void 0 : _data$results.residents) !== null && _data$results$residen !== void 0 ? _data$results$residen : []).filter(h => h.id && h.accountId && h.title).map(h => ({
    residencyId: h.id,
    accountId: h.accountId,
    label: h.title,
    detail: h.subtitle
  }));
};
/** Current residents with a portal account, by name, for the dropdown. */
export const currentResidentsFrom = function () {
  let rows = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return rows.filter(r => {
    var _r$fullname;
    return r.residencyId && r.accountId && ((_r$fullname = r.fullname) === null || _r$fullname === void 0 ? void 0 : _r$fullname.trim()) && (!r.status || r.status === 'Current');
  }).map(r => ({
    residencyId: r.residencyId,
    accountId: r.accountId,
    label: r.fullname.trim(),
    detail: [r.unitName && "Unit ".concat(r.unitName), r.unitPropertyAddress].filter(Boolean).join(' · ') || undefined
  })).sort((a, b) => a.label.localeCompare(b.label));
};

/** The selected company's current residents across its active properties, through the app's own APIs. */
async function loadCurrentResidents() {
  var _props$response, _resp$response;
  const companyId = localStorage.getItem(USER_SETTINGS_KEYS.company) || '';
  const options = {};
  const props = await api.Companies.getCompanyPropertyList({
    companyId
  }, options);
  const propertyIds = ((_props$response = props === null || props === void 0 ? void 0 : props.response) !== null && _props$response !== void 0 ? _props$response : []).filter(p => !p.propertyStatus || p.propertyStatus === 'Active').map(p => p.propertyId);
  if (!propertyIds.length) return [];
  const resp = await api.Residencies.searchResidenciesAggregate({
    portfolioIds: [],
    propertyIds,
    statuses: ['Current'],
    includeEmptyUnits: false,
    sortBy: 'Fullname',
    sortOrder: 'Asc',
    // ponytail: one page of 1000; past that the remote search below still finds anyone.
    page: 1,
    pageSize: 1000
  }, options);
  return currentResidentsFrom(resp === null || resp === void 0 || (_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.buildingResidency);
}
export function LivyResidentPicker(_ref) {
  let companyLabel = _ref.companyLabel,
    onPick = _ref.onPick;
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    query = _useState2[0],
    setQuery = _useState2[1];
  const _useState3 = useState([]),
    _useState4 = _slicedToArray(_useState3, 2),
    options = _useState4[0],
    setOptions = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    loading = _useState6[0],
    setLoading = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    failed = _useState8[0],
    setFailed = _useState8[1];
  // The company's current residents, loaded once; null until loaded, or when the load failed (name search then).
  const _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    current = _useState0[0],
    setCurrent = _useState0[1];
  useEffect(() => {
    if (!companyLabel) return undefined;
    let live = true;
    setLoading(true);
    loadCurrentResidents().then(list => live && setCurrent(list)).catch(() => live && setCurrent(null)).finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [companyLabel]);
  useEffect(() => {
    const config = livyConfig();
    // With the list loaded, typing filters it here; the server search is only the fallback.
    if (current !== null && current !== void 0 && current.length || !config || !shouldSearch(query)) {
      setOptions([]);
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      setFailed(false);
      try {
        const token = await tokenForCompany();
        if (!token) throw new Error('no session');
        const res = await livyFetch(config, token, {
          method: 'GET',
          path: "/search?q=".concat(encodeURIComponent(query), "&scope=company"),
          signal: controller.signal
        });
        if (!res.ok) throw new Error(String(res.status));
        if (!controller.signal.aborted) setOptions(residentsFrom(await res.json()));
      } catch (_unused) {
        if (!controller.signal.aborted) {
          setOptions([]);
          setFailed(true);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }, 250);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [query, current]);
  const list = current !== null && current !== void 0 && current.length ? current : options;
  if (!companyLabel) {
    return /*#__PURE__*/React.createElement(Typography, {
      variant: "body2",
      color: "text.secondary",
      align: "center"
    }, "Choose a customer above, then one of its residents.");
  }
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      width: '100%',
      maxWidth: 420
    }
  }, /*#__PURE__*/React.createElement(Autocomplete, {
    options: list,
    loading: loading,
    openOnFocus: true,
    filterOptions: (xs, state) => {
      if (!(current !== null && current !== void 0 && current.length)) return xs;
      const needle = state.inputValue.trim().toLowerCase();
      return (needle ? xs.filter(o => {
        var _o$detail;
        return "".concat(o.label, " ").concat((_o$detail = o.detail) !== null && _o$detail !== void 0 ? _o$detail : '').toLowerCase().includes(needle);
      }) : xs).slice(0, 200);
    },
    getOptionLabel: o => o.label,
    isOptionEqualToValue: (a, b) => a.residencyId === b.residencyId
    // Picking an option writes its label back as input; that is not a new search.
    ,
    onInputChange: (_, v, reason) => reason !== 'reset' && setQuery(v),
    onChange: (_, v) => v && onPick(v),
    noOptionsText: loading ? 'Loading residents…' : failed ? 'Search failed. Try again.' : current !== null && current !== void 0 && current.length || shouldSearch(query) ? 'No resident matched.' : 'Type a name to search.',
    renderOption: (props, o) => /*#__PURE__*/React.createElement("li", _extends({}, props, {
      key: o.residencyId
    }), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Typography, {
      variant: "body2"
    }, o.label), o.detail && /*#__PURE__*/React.createElement(Typography, {
      variant: "caption",
      color: "text.secondary"
    }, o.detail))),
    renderInput: params => /*#__PURE__*/React.createElement(TextField, _extends({}, params, {
      size: "small",
      label: "Resident at ".concat(companyLabel),
      placeholder: current !== null && current !== void 0 && current.length ? "".concat(current.length, " current residents. Type to filter") : 'Search by name'
    }))
  }));
}